import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import lpApi from '../../helpers/lpApi';

// ============================================================================
// ReportProblemModal — consumer satisfaction-guarantee CLAIM entry for a
// COMPLETED standard (on-demand / scheduled) booking. Backs the Refund Policy
// promise that a client can "request a refund through the app".
//
// Talks to besthand-api (the single source of truth for BookingClaim) through
// the existing `/lp-graphql` proxy via `lpApi`, exactly like the LP module —
// the proxy forwards the logged-in client's auth so besthand-api populates
// request.user. On open we fetch any existing claim (myBookingClaim) and show
// its status; otherwise we show the reasonCode + description form
// (openBookingClaim). NOT gated by largeProjectsEnabled.
// ============================================================================

const REASONS = [
  { code: 'WORK_NOT_AS_AGREED', label: 'Work was not as agreed' },
  { code: 'INCOMPLETE', label: 'Job was incomplete' },
  { code: 'QUALITY', label: 'Poor quality of work' },
  { code: 'DELAY', label: 'Significant delay' },
  { code: 'UNAUTHORIZED_CHARGE', label: 'Unauthorized charge' },
  { code: 'NOT_PERFORMED', label: 'Service was not performed' },
  { code: 'OTHER', label: 'Other' },
];

const Q_MY_CLAIM = `
  query ($bookingId: Int!) {
    myBookingClaim(bookingId: $bookingId) {
      status errorMessage
      claim { id bookingId reasonCode description status resolutionType refundAmount adminNotes createdAt }
    }
  }
`;

const M_OPEN = `
  mutation ($bookingId: Int!, $reasonCode: String!, $description: String) {
    openBookingClaim(bookingId: $bookingId, reasonCode: $reasonCode, description: $description) {
      status errorMessage
      claim { id status reasonCode description createdAt }
    }
  }
`;

const STATUS_LABEL = {
  OPEN: 'Open — our team will review it shortly.',
  UNDER_REVIEW: 'Under review by our team.',
  RESOLVED: 'Resolved.',
  DENIED: 'Reviewed — not eligible for a refund.',
};

const S = {
  intro: { color: '#6b7280', fontSize: 14, marginBottom: 16, lineHeight: 1.5 },
  label: { display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#0f1214' },
  select: { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e7e9ec', fontSize: 14, marginBottom: 14, background: '#fff' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e7e9ec', fontSize: 14, minHeight: 90, marginBottom: 16, resize: 'vertical', fontFamily: 'inherit' },
  btn: { background: '#e23744', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 800, cursor: 'pointer', width: '100%' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  claimCard: { border: '1px solid #e7e9ec', borderRadius: 12, padding: '16px 18px' },
  claimStatus: { fontSize: 16, fontWeight: 800, margin: '0 0 6px' },
  meta: { color: '#6b7280', fontSize: 13, lineHeight: 1.6 },
};

function firstError(res) {
  return res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.';
}

function ReportProblemModal({ show, onHide, bookingId }) {
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [reasonCode, setReasonCode] = useState('WORK_NOT_AS_AGREED');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_MY_CLAIM, { bookingId: Number(bookingId) });
      if (res.errors) { setMsg({ error: firstError(res) }); }
      else {
        const env = res.data && res.data.myBookingClaim;
        if (env && env.status === 200) setExisting(env.claim || null);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load: ${e.message}` });
    }
    setLoading(false);
  }, [bookingId]);

  useEffect(() => {
    if (show) {
      setExisting(null);
      setReasonCode('WORK_NOT_AS_AGREED');
      setDescription('');
      load();
    }
  }, [show, load]);

  const submit = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await lpApi(M_OPEN, {
        bookingId: Number(bookingId),
        reasonCode,
        description: description.trim() ? description.trim() : null,
      });
      if (res.errors) { setMsg({ error: firstError(res) }); setSubmitting(false); return; }
      const env = res.data && res.data.openBookingClaim;
      if (env && env.status === 200) {
        setExisting(env.claim);
        setMsg({ ok: 'Thanks — your report has been submitted. Our team will review it.' });
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not submit: ${e.message}` });
    }
    setSubmitting(false);
  };

  const isOpen = existing && (existing.status === 'OPEN' || existing.status === 'UNDER_REVIEW');

  return (
    <Modal show={show} animation={false} onHide={onHide} dialogClassName={'commonModalContainer'}>
      <Modal.Header closeButton className={'commonModalHeader'}>
        <Modal.Title>
          <div className={'commonModalTitle'}>
            <span>Report a problem</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'commonModalBody'}>
        <div>
          {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}

          {loading ? (
            <div style={S.meta}>Loading…</div>
          ) : existing ? (
            <div style={S.claimCard}>
              <p style={S.claimStatus}>{STATUS_LABEL[existing.status] || existing.status}</p>
              <div style={S.meta}>
                Reason: {(REASONS.find((r) => r.code === existing.reasonCode) || {}).label || existing.reasonCode}<br />
                {existing.description ? <>You wrote: “{existing.description}”<br /></> : null}
                {existing.refundAmount != null ? <>Refund: ${Number(existing.refundAmount).toFixed(2)}<br /></> : null}
                {existing.adminNotes ? <>Note: {existing.adminNotes}<br /></> : null}
              </div>
              {!isOpen && (
                <p style={{ ...S.meta, marginTop: 10 }}>
                  This claim has been closed. Contact support if you still need help.
                </p>
              )}
            </div>
          ) : (
            <>
              <p style={S.intro}>
                Not happy with this completed job? Tell us what went wrong and our team will review it.
              </p>
              <label style={S.label}>What happened?</label>
              <select style={S.select} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                {REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </select>
              <label style={S.label}>Describe the problem (optional)</label>
              <textarea
                style={S.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add any details that help us understand the issue…"
              />
              <button
                style={submitting ? { ...S.btn, ...S.btnDisabled } : S.btn}
                disabled={submitting}
                onClick={submit}
              >
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ReportProblemModal;
